/**
 * PermissionGridCell Component
 * Memoized checkbox cell for the permission grid matrix
 * Optimized for performance with many cells
 */

import React, { memo, useCallback } from 'react';
import { colors } from '../../theme';
import { LockedMark, lockedBand } from '../base/Locked';

export interface PermissionGridCellProps {
  roleId: string;
  permissionKey: string;
  isChecked: boolean;
  isDisabled?: boolean;
  /** A permission the current user cannot give and this role does not have:
   * an amber lock stands where the checkbox would be. Tapping it opens the
   * reason (onLockedPress). */
  isLocked?: boolean;
  /** A permission the current user cannot give that this role already has:
   * still a real checkbox, on the locked row's amber band. */
  isKept?: boolean;
  /** Spoken name of the lock: role, permission, "Can't give" and the reason. */
  lockedLabel?: string;
  /** Whether this lock's reason is open (aria-expanded). */
  lockedExpanded?: boolean;
  /** Tap on the lock: open its reason. */
  onLockedPress?: (roleId: string, permissionKey: string) => void;
  /** Accessible name for the checkbox, e.g. "Technician: Edit invoices". */
  ariaLabel?: string;
  /** id of the element explaining a lock (read out by screen readers). */
  describedBy?: string;
  onChange: (roleId: string, permissionKey: string, checked: boolean) => void;
}

/**
 * Memoized checkbox cell that only re-renders when its specific state changes
 */
export const PermissionGridCell = memo(function PermissionGridCell({
  roleId,
  permissionKey,
  isChecked,
  isDisabled: isDisabledProp = false,
  isLocked = false,
  isKept = false,
  lockedLabel,
  lockedExpanded,
  onLockedPress,
  ariaLabel,
  describedBy,
  onChange,
}: PermissionGridCellProps) {
  const isDisabled = isDisabledProp || isLocked;
  const handleChange = useCallback(() => {
    onChange(roleId, permissionKey, !isChecked);
  }, [roleId, permissionKey, isChecked, onChange]);

  const border = {
    padding: '4px 8px',
    textAlign: 'center' as const,
    borderBottom: `1px solid ${colors.border.default}`,
    borderRight: `1px solid ${colors.border.default}`,
  };

  if (isLocked) {
    // Not a checkbox at all: a lock you can tap for the reason.
    return (
      <td
        style={{ ...border, ...lockedBand('transparent', true), cursor: 'pointer' }}
        onClick={() => onLockedPress?.(roleId, permissionKey)}
        data-testid={`permission-cell-${roleId}-${permissionKey}`}
        data-locked
      >
        <LockedMark
          label={lockedLabel || ariaLabel || "Can't give"}
          onPress={() => onLockedPress?.(roleId, permissionKey)}
          expanded={lockedExpanded}
          controls={lockedExpanded ? describedBy : undefined}
          testId={`permission-locked-cell-${roleId}-${permissionKey}`}
          style={{ verticalAlign: 'middle' }}
        />
      </td>
    );
  }

  return (
    <td
      style={{
        ...border,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...(isKept
          ? lockedBand(isChecked ? 'rgba(59, 130, 246, 0.1)' : 'transparent')
          : { backgroundColor: isChecked ? 'rgba(59, 130, 246, 0.1)' : 'transparent' }),
        transition: 'background-color 0.15s ease',
      }}
      onClick={isDisabled ? undefined : handleChange}
      data-testid={`permission-cell-${roleId}-${permissionKey}`}
      data-kept={isKept || undefined}
    >
      <input
        type="checkbox"
        checked={isChecked}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-describedby={isKept ? describedBy : undefined}
        onChange={handleChange}
        style={{
          width: '16px',
          height: '16px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          accentColor: colors.brand.primary,
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </td>
  );
});

export default PermissionGridCell;
